import React, { useState, useRef, useEffect } from 'react';
import { Typography, Row, Col, Card, Carousel, Button, Modal } from 'antd';
import { LeftOutlined, RightOutlined, FullscreenOutlined, CloseOutlined } from '@ant-design/icons';
import { useSwipeable } from 'react-swipeable';

const { Title } = Typography;

const ApartmentPhotos = () => {
  const photoContext = require.context('../img/apartmentPhotos', false, /\.(png|jpe?g|svg)$/);
  const photoFiles = photoContext.keys();

  const carouselRef = useRef();
  const modalCarouselRef = useRef();
  const [activeSlide, setActiveSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('');

  // Group photos by their prefix
  const groupPhotos = () => {
    const groups = {
      'Photos': [] // Default group for ungrouped photos
    };
    
    photoFiles.forEach(file => {
      const match = file.match(/^\.\/(\w+)_\d+/);
      if (match) {
        const category = match[1].split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(photoContext(file));
      } else {
        // If no pattern match, add to default Photos group
        groups['Photos'].push(photoContext(file));
      }
    });

    // If Photos group is empty, remove it
    if (groups['Photos'].length === 0) {
      delete groups['Photos'];
    }

    // Return groups with Photos first if it exists
    return Object.entries(groups).sort(([a], [b]) => 
      a === 'Photos' ? -1 : b === 'Photos' ? 1 : a.localeCompare(b)
    ).reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
  };

  const photoGroups = groupPhotos();

  const handleThumbnailClick = (groupIndex, photoIndex, globalIndex) => {
    carouselRef.current.goTo(globalIndex);
    setActiveSlide(globalIndex);
  };

  // Flatten all photos for the main carousel
  const allPhotos = Object.values(photoGroups).flat();

  const handleNext = () => {
    carouselRef.current.next();
  };

  const handlePrev = () => {
    carouselRef.current.prev();
  };

  const handleShowModal = () => {
    setIsModalOpen(true);
    // Ensure modal carousel shows the same photo as the main carousel
    setTimeout(() => {
      if (modalCarouselRef.current) {
        modalCarouselRef.current.goTo(activeSlide, false);
      }
    }, 100);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalNext = () => {
    modalCarouselRef.current.next();
  };

  const handleModalPrev = () => {
    modalCarouselRef.current.prev();
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft') {
        if (isModalOpen) {
          handleModalPrev();
        } else {
          handlePrev();
        }
      } else if (event.key === 'ArrowRight') {
        if (isModalOpen) {
          handleModalNext();
        } else {
          handleNext();
        }
      } else if (event.key === 'Escape' && isModalOpen) {
        handleModalClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isModalOpen]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const modalSwipeHandlers = useSwipeable({
    onSwipedLeft: () => handleModalNext(),
    onSwipedRight: () => handleModalPrev(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  return (
    <Card style={{ margin: '24px' }}>
      <div className="property-info">
        {/* Categories Section - Modified for full width */}
        <div style={{ 
          marginBottom: '24px',
          borderBottom: '1px solid #f0f0f0',
          padding: '16px 0',
          width: '100%',
        }}>
          <Row 
            style={{ 
              width: '100%',
              display: 'flex',
              margin: 0
            }}
          >
            {Object.entries(photoGroups).map(([category], index) => {
              const isActive = category === activeCategory;
              const photoCount = photoGroups[category].length;
              const totalCategories = Object.keys(photoGroups).length;
              
              return (
                <Col 
                  key={category} 
                  style={{
                    flex: 1,
                    padding: '0 4px',
                    width: `${100 / totalCategories}%`
                  }}
                >
                  <div
                    onClick={() => {
                      setActiveCategory(category);
                      carouselRef.current.goTo(index * 3);
                    }}
                    style={{
                      cursor: 'pointer',
                      padding: '12px 8px',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#1890ff' : '#f5f5f5',
                      transition: 'all 0.3s ease',
                      textAlign: 'center',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: isActive ? 'none' : '1px solid #e8e8e8',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    <Title
                      level={4}
                      style={{
                        margin: 0,
                        color: isActive ? 'white' : 'rgba(0, 0, 0, 0.85)',
                        fontSize: '14px',
                        fontWeight: isActive ? '600' : '400',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {`${category} (${photoCount})`}
                    </Title>
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Main Carousel */}
        <div style={{ position: 'relative' }}>
          <Button
            icon={<LeftOutlined />}
            onClick={handlePrev}
            style={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              opacity: 0.8,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          />
          <Button
            icon={<RightOutlined />}
            onClick={handleNext}
            style={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              opacity: 0.8,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          />
          <Button
            icon={<FullscreenOutlined />}
            onClick={handleShowModal}
            style={{
              position: 'absolute',
              right: 20,
              top: 20,
              zIndex: 2,
              opacity: 0.8,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: 40,
              height: 40,
            }}
          />
          <div 
            {...swipeHandlers}
            style={{
              height: '60vh',
              position: 'relative',
              backgroundColor: 'transparent',
            }}
          >
            <Carousel
              ref={carouselRef}
              beforeChange={(from, to) => setActiveSlide(to)}
              dots={false}
              draggable
              slidesToShow={1}
              slidesToScroll={1}
              infinite={true}
            >
              {allPhotos.map((photo, index) => (
                <div key={index}>
                  <div 
                    style={{
                      height: '60vh',
                      backgroundImage: `url(${photo})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      marginBottom: 24,
                      cursor: 'grab',
                    }}
                    onClick={handleShowModal} // Make the image clickable to open modal
                  />
                </div>
              ))}
            </Carousel>
            <div className="photo-counter" style={{
              position: 'absolute',
              bottom: 20,
              right: 20,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '4px 12px',
              borderRadius: 12,
              zIndex: 3
            }}>
              {`${activeSlide + 1} / ${allPhotos.length}`}
            </div>
          </div>
        </div>

        {/* Thumbnails Section */}
        <div style={{ 
          overflowX: 'auto', 
          padding: '20px 0',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f0f0f0',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '3px',
          }
        }}>
          <div style={{ display: 'flex', gap: '10px', minWidth: 'min-content' }}>
            {Object.entries(photoGroups).map(([category, photos], groupIndex) => (
              <React.Fragment key={category}>
                {photos.map((photo, photoIndex) => {
                  const globalIndex = Object.values(photoGroups)
                    .flat()
                    .findIndex(p => p === photo);
                  return (
                    <div
                      key={`${category}-${photoIndex}`}
                      onClick={() => handleThumbnailClick(groupIndex, photoIndex, globalIndex)}
                      style={{
                        width: 100,
                        height: 70,
                        flexShrink: 0,
                        backgroundImage: `url(${photo})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        cursor: 'pointer',
                        border: activeSlide === globalIndex ? '3px solid #1890ff' : '3px solid transparent',
                        borderRadius: '4px',
                        transition: 'border-color 0.3s ease'
                      }}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Photo Modal */}
      <Modal
        visible={isModalOpen}
        footer={null}
        onCancel={handleModalClose}
        width="90%"
        centered
        style={{ maxWidth: '1200px' }}
        bodyStyle={{ padding: 0 }}
        closeIcon={
          <CloseOutlined style={{ 
            color: 'white', 
            fontSize: '20px', 
            backgroundColor: 'rgba(0, 0, 0, 0.5)', 
            borderRadius: '50%',
            padding: '8px',
            position: 'absolute',
            top: '20px',
            right: '20px',
            zIndex: 10
          }} />
        }
      >
        <div style={{ position: 'relative', backgroundColor: 'black' }}>
          <Button
            icon={<LeftOutlined />}
            onClick={handleModalPrev}
            style={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              opacity: 0.8,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          />
          <Button
            icon={<RightOutlined />}
            onClick={handleModalNext}
            style={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              opacity: 0.8,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          />
          <div {...modalSwipeHandlers}>
            <Carousel
              ref={modalCarouselRef}
              beforeChange={(from, to) => setActiveSlide(to)}
              dots={false}
              draggable
              slidesToShow={1}
              slidesToScroll={1}
              infinite={true}
            >
              {allPhotos.map((photo, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <div style={{
                    height: '85vh',
                    width: '100%',
                    backgroundImage: `url(${photo})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    cursor: 'grab',
                  }} />
                </div>
              ))}
            </Carousel>
          </div>
          <div className="photo-counter" style={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: 12,
            zIndex: 3
          }}>
            {`${activeSlide + 1} / ${allPhotos.length}`}
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default ApartmentPhotos;
